import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid2';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import * as React from 'react';
import { APIContext } from '../../Contexts/APIContext';
import ResponsiveTable from '../../Utilities/ResponsiveTable';

import UserManagementRow from './OrganisationUserManagmentTableRow';
import UserTableRowViewModel from './ViewModels/UserTableRowViewModel';
import UserTableViewModel from './ViewModels/UserTableViewModel';

import AddOrgAdminDialog from './CreateOrgUserDialog';
import { useContext, useEffect, useState } from 'react';
import EditOrgUserDialog from './EditOrgUser';
import useDebounce from '../../Utilities/Hooks/UseDebounce';

interface IProps {
    orgId: string
}

const tableHeaders: string[] = [
    "Name",
    "Email",
    "Role",
    "Site",
    "Office status",
];

export default function OrgUserManagmentTable(props: IProps) {
    const { orgId } = props;
    const { get } = useContext(APIContext);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [result, setResult] = useState<UserTableViewModel>(new UserTableViewModel());
    const [search, setSearch] = useState('');
    const [archivedOnly, setArchivedOnly] = useState(false);
    const [loading, setLoading] = useState(true);
    const [openAdd, setOpenAdd] = useState<boolean>(false);
    const [editId, setEditId] = useState<string | null>(null);

    useEffect(() => {
        if (orgId) {
            getData();
        }
    }, [page, rowsPerPage, search, archivedOnly, orgId]);

    const getData = async () => {
        setLoading(true);
        get("AdminUser", `GetUsersTable?page=${page}&pageRows=${rowsPerPage}&search=${encodeURIComponent(search)}&orgId=${orgId}&archivedOnly=${archivedOnly}`, setResult);
        setLoading(false);
    };

    const closeDialogs = (refresh: boolean) => {
        getData();
        setOpenAdd(false);
        setEditId(null);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearch(event.target.value);
        setPage(0);
    };

    const handleArchivedOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setArchivedOnly(event.target.checked);
        setPage(0);
    };

    const { debouncedFunction: debouncedhandleSearch } = useDebounce(
        (data: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            handleSearch( data );
        },
        600
    );

    return (
        <>
            <Grid container>
                <Grid container size={12}>
                    <Grid size={5}>
                        <TextField
                            fullWidth
                            color="primary"
                            size="small"
                            variant="outlined"
                            onChange={debouncedhandleSearch}
                            placeholder="Search..."
                            InputProps={{
                                endAdornment: <SearchIcon sx={{ color: '#B5ADC4' }} />,
                            }}
                        />
                    </Grid>
                    <Grid size={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={archivedOnly}
                                    onChange={handleArchivedOnlyChange}
                                    size="small"
                                />
                            }
                            label="Archived only"
                        />
                    </Grid>
                    <Grid >
                        <Button size="small" onClick={() => setOpenAdd(true)} fullWidth={false}>
                            Add User
                        </Button>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <TableContainer sx={{ width: "100%" }}>
                        <ResponsiveTable>
                            <TableHead>
                                <TableRow>
                                    {tableHeaders.map((header: string) => (
                                        <TableCell key={header}>{header}</TableCell>
                                    ))}
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ width: "100%" }}>
                                {(!loading && result) &&
                                    result.rows.map((row: UserTableRowViewModel) => {
                                        return (<UserManagementRow key={row.id} row={row} refresh={getData} edit={setEditId} />);
                                    })
                                }
                                {loading &&
                                    <TableRow
                                        sx={{
                                            height: 53 * rowsPerPage,
                                        }}
                                    >
                                        <TableCell colSpan={tableHeaders.length + 1} align="center"><CircularProgress color="primary" /></TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </ResponsiveTable>
                    </TableContainer>
                    {((!loading && result)) &&
                        <Grid>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={result.count}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Grid>
                    }
                </Grid>
            </Grid >
            <AddOrgAdminDialog open={openAdd} onClose={closeDialogs} orgId={orgId} role="Choose Role" />
            <EditOrgUserDialog open={editId != null} onClose={closeDialogs} userId={editId} orgId={orgId} showRoles={true} />
        </>
    );
}
